import customFormatters from '@/filters/customFormatters'
import authService, { USER_ROLES } from '@/api/authService'
import siteConfig from '@/mixins/siteConfig'
import { mapMutations, mapGetters } from 'vuex'
import { GETTERS, MODULE_NAME, MUTATIONS } from '@/store/filters/constants'
import locationsService from '@/api/locationsService'
import debounce from '@/utils/debounce'
import dashboardService from '@/api/dashboardService'
import campaignsService from '@/api/campaignsService'
import { CHANNEL_OPTIONS, CHANNEL_COLORS, CHANNEL_LABELS } from '@/utils/campaignOptions'
import i18n from '@/i18n'

const DEBOUNCE_TIME = 500
const allOption = () => ({ id: 'all', name: i18n.t('ALL_MY_CENTERS') })
const initialChannels = () => Object.keys(CHANNEL_OPTIONS)
  .map(key => ({
    type: key,
    campaigns: 0,
    kpis: [],
    zones: [],
    budget: 0,
    loading: true,
    label: CHANNEL_LABELS[key],
    color: CHANNEL_COLORS[key]
  }))

const initialChannelsBudget = () => Object.keys(CHANNEL_OPTIONS)
  .map(key => ({
    type: key,
    budget: 0,
    label: CHANNEL_LABELS[key],
    color: CHANNEL_COLORS[key]
  }))

export default {
  name: 'Summary',
  mixins: [
    siteConfig,
    customFormatters
  ],
  components: {
    Carousel: () => import('@/components/carousel/Carousel.vue'),
    ChannelCard: () => import('@/components/channel-card/ChannelCard.vue'),
    Sunburst: () => import('@/components/sunburst/Sunburst.vue'),
    BillingNotifycation: () => import('@/components/billing-notification/BillingNotification.vue'),
    CampaignCreationButton: () => import('@/components/campaign-creation-button/CampaignCreationButton.vue')
  },
  props: {},
  data () {
    return {
      authData: authService.authData,
      stores: [],
      selectedStore: allOption(),
      channels: initialChannels(),
      channelsBudgetData: initialChannelsBudget(),
      selectedChannelType: null,
      specificBudget: 0,
      plannedBudget: 0,
      noCampaigns: 0
    }
  },
  computed: {
    ...mapGetters(MODULE_NAME, {
      filters: GETTERS.GET_ROUTE_FILTERS
    }),
    currentYear () {
      return new Date().getFullYear()
    },
    user () {
      return this.authData.user
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    isExteriorAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY_EXTERIOR
    },
    isPatrimony () {
      return this.authData.user.role === USER_ROLES.AGENCY_PATRIMONY
    },
    canCreateCamapign () {
      return this.authData.user.role !== USER_ROLES.AGENCY
    },
    allStoresSelected () {
      return this.selectedStore.id === 'all'
    },
    numAdvertsActive () {
      if (this.allStoresSelected) {
        return this.stores.reduce((acum, elem) => acum + elem.no_adverts || 0, 0)
      } else {
        return this.selectedStore.no_adverts || 0
      }
    },
    numAdvertsIncidence () {
      if (this.allStoresSelected) {
        return this.stores.reduce((acum, elem) => acum + elem.no_adverts_incidence || 0, 0)
      } else {
        return this.selectedStore.no_adverts_incidence || 0
      }
    },
    totalBudget () {
      const budgets = this.allStoresSelected
        ? this.stores.map(s => s.shop_budgets).flat()
        : this.selectedStore.shop_budgets

      let total = 0
      for (const b of budgets) {
        if (b && b.year === this.currentYear) {
          total += Number(b.budget)
        }
      }

      return total
    },
    spentConsumedBudget () {
      return this.allStoresSelected
        ? this.stores.reduce((acum, elem) => acum + (elem.budget || 0), 0)
        : this.selectedStore.budget
    },
    spentPlannedBudget () {
      return this.allStoresSelected
        ? this.stores.reduce((acum, elem) => acum + (elem.budget || 0), 0)
        : this.selectedStore.budget
    },
    remainingBudget () {
      return this.totalBudget - this.spentConsumedBudget
    },
    chartData () {
      return this.channelsBudgetData.map(c => ({
        id: c.type,
        name: c.label,
        color: c.color,
        budget: c.budget || 0
      }))
    },
    campaignsCount () {
      return this.noCampaigns
    },
    channelsBudget () {
      const sumOfChannelBudgets = this.channelsBudgetData.reduce((acum, elem) => acum + elem.budget, 0 || 0)

      return this.selectedChannelType
        ? this.channelsBudgetData.find(c => c.type === this.selectedChannelType).budget
        : this.specificBudget + sumOfChannelBudgets
    },
    channelsPlannedBudget () {
      return this.plannedBudget
    },
    channelsBudgetPercent () {
      return (this.channelsBudget / (this.plannedBudget || 1)) * 100
    },
    selectedChannel () {
      return this.channels.find(c => c.type === this.selectedChannelType)
    },
    counterColor () {
      return this.selectedChannelType
        ? this.selectedChannel.color
        : 'var(--colorPrimary)'
    },
    loading () {
      return this.channels.some(c => c.loading)
    },
    summaryChannelFilter () {
      return this.$store.state.filters.filters.find(f => f.key === 'campaign_channel_summary')
    },
    genericChannelFilter () {
      return this.$store.state.filters.filters.find(f => f.key === 'campaign_channel')
    }
  },
  watch: {
    isExteriorAgency: {
      immediate: true,
      handler (flag) {
        if (flag) {
          this.$router.replace(`/campaigns`)
        }
      }
    },
    isPatrimony: {
      immediate: true,
      handler (flag) {
        if (flag) {
          this.$router.replace(`/map`)
        }
      }
    },
    filters (filters) {
      this.selectedChannelType = this.genericChannelFilter.value || null
      this.fetchCampaigns(filters)
      this.fetchSpecificBilling(filters)
      this.fetchSpecificChannelBilling(filters)
      this.fetchPlannedBilling(filters)
      this.fetchStats(filters)
    },
    loading (flag) {
      if (!flag) {
        this.$refs.sunburst.drawChart()
      }
    }
  },
  beforeDestroy () {
    this[MUTATIONS.UPDATE_FILTER_DEF]({
      key: 'campaign_channel',
      display: 'button-group',
      multiple: true
    })
  },
  async mounted () {
    this[MUTATIONS.UPDATE_FILTER_DEF]({
      key: 'campaign_channel',
      display: 'toggle-group',
      multiple: false
    })
    this._searchShops = debounce(this.searchShops, DEBOUNCE_TIME)
    await this.searchShops('')
  },
  methods: {
    ...mapMutations(MODULE_NAME, [
      MUTATIONS.SET_FILTER_VALUE,
      MUTATIONS.UPDATE_FILTER_DEF
    ]),
    fetchStats (filters) {
      this.channels.forEach(c => {
        dashboardService.getChannelTypeStatsSummary({ type: c.type }, filters || this.filters).then(data => {
          c.campaigns = data.count
          c.kpis = data.metrics
          c.zones = data.zones
          c.budget = data.budget
          c.loading = false
        })
      })
    },
    fetchSpecificBilling (filters) {
      if (!this.selectedChannelType) {
        campaignsService.getBillingTotal(filters || this.filters).then(data => {
          this.specificBudget = data
        })
      }
    },
    fetchSpecificChannelBilling (filters) {
      this.channelsBudgetData.forEach(cbd => {
        campaignsService.getBillingTotal(filters || this.filters, cbd.type).then(data => {
          cbd.budget = data
        })
      })
    },
    fetchPlannedBilling (filters, type = this.selectedChannelType) {
      let extras = ''
      if (type) {
        // NOTE: To prevent cancel the request
        extras = `campaign_channel=${type}&t=planned`
      }
      campaignsService.getPlannedBillingTotal(filters || this.filters, extras).then(data => {
        this.plannedBudget = data
      })

      this.channelsBudgetData.forEach(cbd => {
        let extras = `campaign_channel=${cbd.type}`

        campaignsService.getPlannedBillingTotal(filters || this.filters, extras).then(data => {
          cbd.budget = data
        })
      })
    },
    fetchCampaigns (filters) {
      let extras = 'status=ACTIVE,FINISHED'
      if (this.isDirector) extras = `${extras}&view=summary`
      campaignsService.getCampaigns({ page_size: 1 }, filters || this.filters, extras)
        .then(data => {
          this.noCampaigns = parseInt(data.meta.count)
        })
    },
    slowShopSearch (query) {
      this._searchShops(query)
    },
    searchShops (query) {
      locationsService.getShops({ q: query || undefined })
        .then(({ results }) => {
          const options = [allOption()].concat(results)
          this.stores = options
          this.stores.forEach(s => {
            if (s.id === 'all') return
            let extras = `ignore_national=true&shop_selected=${s.id}`
            campaignsService.getPlannedBillingTotal(this.filters, extras).then(data => {
              s.budget = data
            })
          })
          if (results.length === 1) {
            this.selectedStore = options[1]
          }
        })
    },
    setStore (store) {
      this.$refs.select.close()
      this.selectedStore = store
    },
    filterByType ({ id }) {
      const isValid = this.channels.some(c => c.type === id)
      this[MUTATIONS.SET_FILTER_VALUE]({ key: 'campaign_channel', value: isValid ? id : null })
    }
  }
}
